const project = require("./config/mix/project-config");
const ttecColors = require("./tailwind.ttec.colors");
const typography = require("./tailwind.ttec.typography");

module.exports = {
  content: project.content,
  important: false,
  plugins: [require("@tailwindcss/aspect-ratio")],
  // Completely override the default theme
  theme: {
    screens: {
      tyd: { max: "24.9375em" }, // 399px
      ty: "25em", // 400px
      xsd: { max: "37.4375em" }, // 599px
      xs: "37.5em", // 600px
      smd: { max: "47.9375em" }, // 767px
      sm: "48em", // 768px
      mdd: { max: "59.9375em" }, // 959px
      md: "60em", // 960px
      lgd: { max: "63.9375em" }, // 1023px
      lg: "64em", // 1024px
      homed: { max: "74.9375em" }, // 1199px
      home: "75em", // 1200px
      xld: { max: "87.4375em" }, // 1399px
      xl: "87.5em", // 1400px,
      coarse: { 'raw' : '(pointer:coarse)' },
      fine: { 'raw' : '(pointer:fine)' },
    },
    fontSize: {
      "2xs": ".625rem", // 10px only used for an icon, replace when TW 2.x JIT is available
      xs: ".75rem", // 12px
      sm: ".875rem", // 14px
      base: "1rem", // 16px
      lg: "1.125rem", // 18px
      xl: "1.25rem", // 20px
      "2xl": "1.5rem", // 24px
      "3xl": "2rem", // 32px replaced 30px
      "4xl": "2.625rem", // 42px replaced 36px
      "5xl": "3.5rem", // 56px replaced 48px
      "6xl": "4rem", // 64px replaced 60px
      "7xl": "5rem", // 80px replaced 72px
      "8xl": "6rem", // 96px
      "9xl": "8rem", // 128px
      36: "2.25rem", // 36px - Used throughout, Avtex carryover though
      42: "2.625rem", // 42px - Avtex only, widely used
      150: "9.375rem", // 150px - only used for statistic, replace when TW 2.x JIT is available
    },
    maxWidth: {
      40: "10rem", // REM
      50: "12.5rem", // REM
      275: "275px",
      375: "375px",
      400: "400px",
      575: "575px",
      670: "670px",
      720: "720px",
      820: "820px", // used for search results
      915: "915px",
      1100: "1100px",
      1150: "1150px",
      1400: "1400px",
      1600: "1600px",
      "1/4": "25%",
      "2/3": "66.665%",
      full: "100%",
    },
    zIndex: {
      auto: "auto",
      "-3": -3,
      "-2": -2,
      "-1": -1,
      0: "0",
      1: 1,
      2: 2,
      10: "10",
      20: "20",
      30: "30",
      40: "40",
      50: "50",
      overlay: 997,
      max: 999,
    },

    fill: {
      current: "currentColor",
      none: "none",
    },

    // extends the default theme
    extend: {
      colors: {
        transparent: "transparent",
        current: "currentColor",
        black: "#000000",
        white: "#ffffff",
        green: "#64d96e",
        yellow: "#ffde01",
        teal: "#00d6bb",
        purple: "#7d71e2",
        steel: "#2b3a42",
        platinum: "#cececd",
        whitesmoke: "#f7f7f7",
        primary: "#236BF4", // same as blue - bright main brand color, for universal templating (links, buttons)
        secondary: "#2b3a42", // same as steel - dark secondary brand color, for universal templates (overlays, dark backgrounds)
        "avtex-green": "#2aa154",
        "global-platinum": "#dfe5e5", // used on universal breadcrumb label, needed in both tailwind configs
        "voicefoundry-orange": "#F26629",
        ...ttecColors,
      },
      fontFamily: { ...typography.fontFamily },
      borderColor: (theme) => ({
        ...theme("colors"),
        default: theme("colors.platinum", "currentColor"),
      }),
      animation: {
        'infinite-scroll': 'infinite-scroll 25s linear infinite',
      },
      keyframes: {
        'infinite-scroll': {
          from: { transform: 'translateX(0)' },
          to: { transform: 'translateX(-100%)' },
        }
      },  
      aspectRatio: {
        "3/4": "3 / 4",
      },
      borderRadius: {
        10: "10px",
        20: "20px",
        xl: "20px",
        48: "48px",
      },
      borderWidth: {
        6: "6px",
        24: "24px",
      },
      boxShadow: {
        default: "10px 10px 0px rgba(0, 0, 0, 0.08)",
        md: "0 6px 10px rgba(0, 0, 0, 0.25)",
      },
      height: {
        "1/2": "50%",
        full: "100%",
      },
      maxHeight: {
        75: "75px",
      },
      letterSpacing: {
        snug: "-.0125em",
      },
      opacity: {
        15: "0.15",
        90: "0.9",
        95: "0.95",
      },
      spacing: {
        0.5: ".125rem", // 2px
        1.5: ".375rem", // 6px
        2.5: ".625rem", // 10px
        7: "1.75rem", // 28px
        9: "2.25rem", // 36px
        15: "3.75rem", // 60px
        18: "4.5rem", // 72px
        18.75: "4.6875rem", // 75px
        30: "7.5rem", // 120px
        36: "9rem", // 144px
        37.5: "9.375rem", // 150px
        43.75: "10.9375rem", // 175px
        50: "12.5rem", // 200px
        52.5: "13.125rem", // 210px
        62.5: "15.625rem", // 250px
        75: "18.75rem", // 300px
        87.5: "21.875rem", // 350px
        90: "22.5rem", // 360px
        105: "26.25rem", // 420px used on spotlight
        "1/2": "50%", // 50%
        "1/3": "33.33%", // 33%
        "1/4": "25%", // 25%
        "7/10": "70vh",
      },
    },
  },
  variants: {
    aspectRatio: ["responsive", "hover"],
    backgroundColor: ({ after }) => after(["group-hover"]),
    backgroundImage: ({ after }) => after(["group-hover"]),
    borderWidth: ({ after }) => after(["first", "last"]),
    borderColor: ({ after }) => after(["focus-visible"]),
    fontStyle: ({ after }) => after(["hover", "group-hover"]),
    height: ({ after }) => after(["group-hover"]),
    margin: ({ after }) => after(["group-hover", "last"]),
    opacity: ({ after }) => after(["group-hover", "disabled"]),
    padding: ({ after }) => after(["hover", "group-hover", "first"]),
    scale: ({ after }) => after(["group-hover"]),
    textColor: ({ after }) => after(["group-hover"]),
    textDecoration: ({ after }) => after(["group-hover"]),
    translate: ({ after }) => after(["group-hover"]),
  },
};
